import { useState, useCallback, useMemo, useEffect } from 'react';
import { useForm } from 'src/components/Form';
import ModalDialog, { ModalCloseHandler } from 'src/components/ModalDialog';
import { useStoreUnitActions } from 'src/redux/helpers';
import { Box, Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import { getRuleConfigurationFormFields } from '../formFields';
import solutionsStore, {
  useAlertSubscriptionOptions,
  useSolutionAssignSiteOptions,
} from '../../redux';
import { SolutionRuleConfigurationItem } from '../../types/solutionRulesConfiguration';

type Props = {
  item?: SolutionRuleConfigurationItem;
  title: string;
  loading?: boolean;
  open: boolean;
  onClose: ModalCloseHandler;
  onSubmit: (values: SolutionRuleConfigurationItem) => void;
  confirmBtnText?: string;
  repositoryType?: string;
};

const containerStyle = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '1rem',
};

function SolutionRuleConfigurationEditModal({
  item,
  title,
  loading,
  open,
  onClose,
  onSubmit,
  confirmBtnText = 'Create',
}: Props) {
  const [isTimeAggregationEnabled, setIsTimeAggregationEnabled] = useState(
    item?.timeAggregation || false
  );
  const [isTargetUrl, setIsTargetUrl] = useState<boolean>(
    item?.actionTypes?.includes('targetURL') || false
  );
  const [options, optionsLoading] = useSolutionAssignSiteOptions();

  const onChange = useCallback(
    ({ timeAggregation, actionTypes }: SolutionRuleConfigurationItem) => {
      setIsTimeAggregationEnabled(timeAggregation);
      setIsTargetUrl(
        Array.isArray(actionTypes) && actionTypes.includes('targetURL')
      );
    },
    [isTimeAggregationEnabled, isTargetUrl]
  );

  const handlePasteAuthorizationToken = () => {
    navigator.clipboard.readText().then((clipboardText) => {
      setValue('sasToken', clipboardText.trim());
    });
  };

  const fields = useMemo(
    () =>
      getRuleConfigurationFormFields(
        isTimeAggregationEnabled,
        isTargetUrl,
        handlePasteAuthorizationToken,
        options,
        optionsLoading
      ),
    [
      isTimeAggregationEnabled,
      isTargetUrl,
      handlePasteAuthorizationToken,
      options,
      optionsLoading,
    ]
  );

  const [form, handleSubmit, setValue] = useForm({
    visibleValuesOnly: true,
    values: item,
    fields,
    onChange,
    sx: containerStyle,
    onSubmit,
  });

  return (
    <ModalDialog
      open={open}
      title={title}
      confirmBtnText={confirmBtnText}
      loading={loading}
      onClose={onClose}
      maxWidth="md"
      titleStyle={{ height: '67px' }}
      showCancelBtn={false}
      showConfirmBtn={false}
      contentStyle={{ paddingRight: '0px' }}
      fullWidth
    >
      {form}
      <Box textAlign="right" mt={3} style={{ paddingRight: '20px' }}>
        <Button onClick={onClose} variant="outlined" sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {!item ? 'Create' : 'Save'}
        </Button>
      </Box>
    </ModalDialog>
  );
}

export default SolutionRuleConfigurationEditModal;
